import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Tag, Banknote, Calendar, Bell, Clock, Repeat, Link as LinkIcon } from 'lucide-react';
import { TASK_PRIORITY, RECURRENCE_TYPE } from '@/services/finTasksService';
import { useTodoLists } from '@/hooks/useTodoLists';
import { CONTEXT_TYPE } from '@/utils/contextLinking';
import { cn } from '@/lib/utils';
import ContextAutocomplete from './ContextAutocomplete';
import { fetchContextOptions, getSmartSuggestions } from '@/utils/contextDataService';
import { auth } from '@/config/firebase';
import toast from 'react-hot-toast';

/**
 * EditTaskModal - Modal for editing existing tasks
 * Reuses AddTaskModal structure but pre-populates with existing task data
 */
export default function EditTaskModal({ isOpen, onClose, onSave, task, prefillListId = null }) {
  const { lists, loading: listsLoading } = useTodoLists();
  
  // Form state - initialize with task data
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    priority: TASK_PRIORITY.MEDIUM,
    dueDate: '',
    reminderAt: '',
    listId: '',
    tags: [],
    isFinancialTask: false,
    estimatedAmount: '',
    linkedType: '',
    linkedRef: '',
    contextLabel: '',
    contextType: 'none',
    contextId: '',
    recurrence: {
      enabled: false,
      type: RECURRENCE_TYPE.NONE,
      interval: 1,
      endDate: ''
    }
  });
  
  const [tagInput, setTagInput] = useState('');
  const [errors, setErrors] = useState({});
  
  // Phase 2.1: Autocomplete state
  const [contextOptions, setContextOptions] = useState([]);
  const [loadingContextOptions, setLoadingContextOptions] = useState(false);

  // Helper: safely format to input datetime-local (YYYY-MM-DDTHH:mm)
  function toInputDateTime(value) {
    if (!value) return '';
    try {
      const d = typeof value === 'string' || value instanceof Date ? new Date(value) : null;
      if (!d || isNaN(d.getTime())) return '';
      return d.toISOString().slice(0, 16);
    } catch {
      return '';
    }
  }

  // Load task data when modal opens or task changes
  useEffect(() => {
    if (isOpen && task) {
      console.log('[EditTaskModal] Available lists:', lists.map(l => ({ id: l.id, title: l.title, icon: l.icon })));
      setFormData({
        title: task.title || '',
        description: task.description || '',
        priority: task.priority || TASK_PRIORITY.MEDIUM,
        dueDate: toInputDateTime(task.dueDate),
        reminderAt: toInputDateTime(task.reminderAt),
        listId: task.listId || prefillListId || '',
        tags: task.tags || [],
        isFinancialTask: task.isFinancialTask || false,
        estimatedAmount: task.estimatedAmount?.toString() || '',
  // Legacy linkedType/linkedRef removed in favor of context linking
        contextLabel: task.contextLabel || '',
        contextType: task.contextType || 'none',
        contextId: task.contextId || '',
        recurrence: task.recurrence && task.recurrence.enabled ? {
          enabled: task.recurrence.enabled,
          type: task.recurrence.type || RECURRENCE_TYPE.NONE,
          interval: task.recurrence.interval || 1,
          endDate: toInputDateTime(task.recurrence.endDate)?.slice(0, 10) || ''
        } : {
          enabled: false,
          type: RECURRENCE_TYPE.NONE,
          interval: 1,
          endDate: ''
        }
      });
      setErrors({});
    }
  }, [isOpen, task, prefillListId]);

  // Phase 2.1: Fetch context options when contextType changes
  useEffect(() => {
    const loadContextOptions = async () => {
      if (formData.contextType === 'none' || !formData.contextType) {
        setContextOptions([]);
        return;
      }

      const userId = auth.currentUser?.uid;
      if (!userId) return;

      setLoadingContextOptions(true);
      
      try {
        // For edit modal, just fetch all options (no smart suggestions needed)
        const options = await fetchContextOptions(userId, formData.contextType);
        setContextOptions(options);
      } catch (error) {
        console.error('[EDIT TASK] Error loading context options:', error);
        toast.error('Failed to load options');
        setContextOptions([]);
      } finally {
        setLoadingContextOptions(false);
      }
    };

    loadContextOptions();
  }, [formData.contextType]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear field error on change
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Phase 2.1: Handle context option selection
  const handleContextSelect = (option) => {
    if (!option) {
      setFormData(prev => ({
        ...prev,
        contextId: '',
        contextLabel: ''
      }));
      return;
    }

    setFormData(prev => ({
      ...prev,
      contextId: option.id,
      contextLabel: option.name
    }));
  };

  const handleRecurrenceChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      recurrence: { ...prev.recurrence, [field]: value }
    }));
  };

  const handleAddTag = () => {
    const tag = tagInput.trim();
    if (tag && !formData.tags.includes(tag)) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tag]
      }));
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }
    
    if (!formData.listId) {
      newErrors.listId = 'Please select a list';
    }
    
    if (formData.dueDate && formData.reminderAt) {
      const due = new Date(formData.dueDate);
      const reminder = new Date(formData.reminderAt);
      if (reminder >= due) {
        newErrors.reminderAt = 'Reminder must be before due date';
      }
    }
    
    if (formData.isFinancialTask && formData.estimatedAmount) {
      const amount = parseFloat(formData.estimatedAmount);
      if (isNaN(amount) || amount < 0) {
        newErrors.estimatedAmount = 'Please enter a valid amount';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    // Build updated task object - only include fields we want to update
    // Include id and listId for the editTask function to work
    const updatedTask = {
      id: task.id,
      title: formData.title.trim(),
      description: formData.description.trim(),
      priority: formData.priority,
      listId: formData.listId,
      tags: formData.tags,
      isFinancialTask: formData.isFinancialTask
    };

    // Add optional fields
    if (formData.dueDate) {
      updatedTask.dueDate = new Date(formData.dueDate);
    } else {
      updatedTask.dueDate = null;
    }

    if (formData.reminderAt) {
      updatedTask.reminderAt = new Date(formData.reminderAt);
    } else {
      updatedTask.reminderAt = null;
    }

    if (formData.isFinancialTask) {
      updatedTask.estimatedAmount = formData.estimatedAmount ? parseFloat(formData.estimatedAmount) : null;
      updatedTask.linkedType = formData.linkedType || null;
      updatedTask.linkedRef = formData.linkedRef || null;
    } else {
      updatedTask.estimatedAmount = null;
      updatedTask.linkedType = null;
      updatedTask.linkedRef = null;
    }

    // Add context linking fields (Phase 2.1)
    if (formData.contextType && formData.contextType !== 'none' && formData.contextLabel) {
      updatedTask.contextLabel = formData.contextLabel;
      updatedTask.contextType = formData.contextType;
      updatedTask.contextId = formData.contextId || '';
    } else {
      // Remove context fields if cleared
      updatedTask.contextLabel = null;
      updatedTask.contextType = null;
      updatedTask.contextId = null;
    }

    if (formData.recurrence.enabled) {
      updatedTask.recurrence = {
        enabled: true,
        type: formData.recurrence.type,
        interval: formData.recurrence.interval,
        endDate: formData.recurrence.endDate ? new Date(formData.recurrence.endDate) : null
      };
    } else {
      updatedTask.recurrence = { enabled: false, type: RECURRENCE_TYPE.NONE };
    }

    await onSave(updatedTask);
    onClose();
  };

  const handleClose = () => {
    setErrors({});
    onClose();
  };

  if (!task) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, y: 100, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 100, scale: 0.9 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="relative w-full max-w-lg bg-white dark:bg-gray-800 rounded-t-2xl sm:rounded-2xl shadow-xl max-h-[90vh] overflow-hidden flex flex-col"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Edit Task
              </h2>
              <button
                onClick={handleClose}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
              >
                <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-5">
              {/* Title */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Task Title
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  placeholder="What needs to be done?"
                  className={`w-full px-4 py-2.5 border ${
                    errors.title ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                  } rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white transition-colors`}
                />
                {errors.title && <p className="mt-1 text-sm text-red-500">{errors.title}</p>}
              </div>

              {/* Description */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Description (Optional)
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder="Add more details..."
                  rows={3}
                  className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white resize-none transition-colors"
                />
              </div>

              {/* Context Label - Link to Budget/Category/List */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  <LinkIcon size={14} className="text-purple-600" />
                  Link to (Optional)
                </label>
                
                {/* Context Type Selector */}
                <div className="flex gap-2 mb-3 flex-wrap">
                  {[
                    { value: 'none', label: 'No Link', icon: '⊘' },
                    { value: CONTEXT_TYPE.BUDGET, label: 'Budget', icon: '💰' },
                    { value: CONTEXT_TYPE.CATEGORY, label: 'Category', icon: '📁' },
                    { value: CONTEXT_TYPE.TRANSACTION, label: 'Transaction', icon: '🧾' },
                    { value: CONTEXT_TYPE.LIST, label: 'List', icon: '🛒' }
                  ].map(type => (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() => {
                        handleInputChange('contextType', type.value);
                        handleInputChange('contextId', '');
                        handleInputChange('contextLabel', '');
                      }}
                      className={cn(
                        'px-3 py-2 rounded-lg text-xs font-semibold transition-all',
                        formData.contextType === type.value
                          ? 'bg-purple-600 text-white shadow-md'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                      )}
                    >
                      {type.icon} {type.label}
                    </button>
                  ))}
                </div>

                {/* Context Label Input - only show if contextType is selected */}
                {formData.contextType !== 'none' && (
                  <>
                    <ContextAutocomplete
                      options={contextOptions}
                      value={formData.contextLabel}
                      onChange={(value) => handleInputChange('contextLabel', value)}
                      onSelect={handleContextSelect}
                      placeholder={`Search ${formData.contextType}s...`}
                      loading={loadingContextOptions}
                      showIcon={formData.contextType === CONTEXT_TYPE.LIST}
                      emptyMessage={loadingContextOptions ? 'Loading...' : `No ${formData.contextType}s found`}
                      className="mb-2"
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      💡 Select from your existing {formData.contextType}s or type to search
                    </p>
                  </>
                )}
              </div>

              {/* Priority */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Priority
                </label>
                <div className="flex gap-2">
                  {[TASK_PRIORITY.LOW, TASK_PRIORITY.MEDIUM, TASK_PRIORITY.HIGH].map((priority) => (
                    <button
                      key={priority}
                      type="button"
                      onClick={() => handleInputChange('priority', priority)}
                      className={`flex-1 px-4 py-2.5 rounded-lg font-medium transition-all ${
                        formData.priority === priority
                          ? priority === TASK_PRIORITY.HIGH
                            ? 'bg-red-500 text-white shadow-lg'
                            : priority === TASK_PRIORITY.MEDIUM
                            ? 'bg-orange-500 text-white shadow-lg'
                            : 'bg-blue-500 text-white shadow-lg'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                      }`}
                    >
                      {priority.charAt(0).toUpperCase() + priority.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              {/* List Selection */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  List
                </label>
                <select
                  value={formData.listId}
                  onChange={(e) => handleInputChange('listId', e.target.value)}
                  className={`w-full px-4 py-2.5 border ${
                    errors.listId ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                  } rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white transition-colors`}
                >
                  <option value="">Select a list</option>
                  {lists.map((list) => (
                    <option key={list.id} value={list.id}>
                      {list.title}
                    </option>
                  ))}
                </select>
                {errors.listId && <p className="mt-1 text-sm text-red-500">{errors.listId}</p>}
              </div>

              {/* Due Date & Reminder */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    <Calendar className="w-4 h-4" />
                    Due Date
                  </label>
                  <input
                    type="datetime-local"
                    value={formData.dueDate}
                    onChange={(e) => handleInputChange('dueDate', e.target.value)}
                    className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white transition-colors"
                  />
                </div>
                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    <Bell className="w-4 h-4" />
                    Reminder
                  </label>
                  <input
                    type="datetime-local"
                    value={formData.reminderAt}
                    onChange={(e) => handleInputChange('reminderAt', e.target.value)}
                    className={`w-full px-4 py-2.5 border ${
                      errors.reminderAt ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                    } rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white transition-colors`}
                  />
                  {errors.reminderAt && <p className="mt-1 text-sm text-red-500">{errors.reminderAt}</p>}
                </div>
              </div>

              {/* Financial Task Toggle */}
              <div className="flex items-center justify-between p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                <div className="flex items-center gap-3">
                  <Banknote className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">Financial Task</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Link to budget or goal</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => handleInputChange('isFinancialTask', !formData.isFinancialTask)}
                  className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${
                    formData.isFinancialTask ? 'bg-purple-600' : 'bg-gray-300 dark:bg-gray-600'
                  }`}
                >
                  <span
                    className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
                      formData.isFinancialTask ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              {/* Financial Details */}
              {formData.isFinancialTask && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="space-y-4 pl-4 border-l-2 border-purple-500"
                >
                  <div>
                    <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Estimated Amount
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.estimatedAmount}
                      onChange={(e) => handleInputChange('estimatedAmount', e.target.value)}
                      placeholder="0.00"
                      className={`w-full px-4 py-2.5 border ${
                        errors.estimatedAmount ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                      } rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white transition-colors`}
                    />
                    {errors.estimatedAmount && <p className="mt-1 text-sm text-red-500">{errors.estimatedAmount}</p>}
                  </div>
                </motion.div>
              )}

              {/* Tags */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  <Tag className="w-4 h-4" />
                  Tags
                </label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                    placeholder="Add a tag..."
                    className="flex-1 px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white transition-colors"
                  />
                  <button
                    type="button"
                    onClick={handleAddTag}
                    className="px-4 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium"
                  >
                    Add
                  </button>
                </div>
                {formData.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {formData.tags.map((tag) => (
                      <span
                        key={tag}
                        className="inline-flex items-center gap-1 px-3 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-full text-sm"
                      >
                        {tag}
                        <button
                          type="button"
                          onClick={() => handleRemoveTag(tag)}
                          className="hover:bg-purple-200 dark:hover:bg-purple-800 rounded-full p-0.5 transition-colors"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Recurrence */}
              <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <Repeat className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">Recurring Task</p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Repeat this task automatically</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRecurrenceChange('enabled', !formData.recurrence.enabled)}
                    className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${
                      formData.recurrence.enabled ? 'bg-purple-600' : 'bg-gray-300 dark:bg-gray-600'
                    }`}
                  >
                    <span
                      className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
                        formData.recurrence.enabled ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                {formData.recurrence.enabled && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="space-y-4 pl-4 border-l-2 border-purple-500"
                  >
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Frequency
                        </label>
                        <select
                          value={formData.recurrence.type}
                          onChange={(e) => handleRecurrenceChange('type', e.target.value)}
                          className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white transition-colors"
                        >
                          <option value={RECURRENCE_TYPE.DAILY}>Daily</option>
                          <option value={RECURRENCE_TYPE.WEEKLY}>Weekly</option>
                          <option value={RECURRENCE_TYPE.MONTHLY}>Monthly</option>
                        </select>
                      </div>
                      <div>
                        <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Every
                        </label>
                        <input
                          type="number"
                          min="1"
                          value={formData.recurrence.interval}
                          onChange={(e) => handleRecurrenceChange('interval', parseInt(e.target.value) || 1)}
                          className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white transition-colors"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        <Clock className="w-4 h-4" />
                        End Date (Optional)
                      </label>
                      <input
                        type="date"
                        value={formData.recurrence.endDate}
                        onChange={(e) => handleRecurrenceChange('endDate', e.target.value)}
                        className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white transition-colors"
                      />
                    </div>
                  </motion.div>
                )}
              </div>
            </form>

            {/* Footer */}
            <div className="flex gap-3 p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
              <button
                type="button"
                onClick={handleClose}
                className="flex-1 px-4 py-2.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={listsLoading}
                className="flex-1 px-4 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium shadow-lg"
              >
                {listsLoading ? 'Loading...' : 'Update Task'}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
